// src/pages/ImageEditorPage.js
// Page for the Batch Image Editor2 with Bulk Upload Functionality

import React, { useState, useEffect, useRef, useCallback } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

const INITIAL_NUM_SLOTS = 12; // Start with 12 slots, but allow growth
const DEFAULT_FONT_SIZE = 22; // Default font size for the final 720px wide output
const DEFAULT_CAPTION_Y = 50; // Default vertical position (50% = center)
const FINAL_CANVAS_WIDTH = 720;
const FINAL_CANVAS_HEIGHT = FINAL_CANVAS_WIDTH * (16 / 9);
const PREVIEW_CANVAS_WIDTH = 135; // Adjusted for better visibility with more slots
const PREVIEW_CANVAS_HEIGHT = PREVIEW_CANVAS_WIDTH * (16 / 9);

// New constants for CTA defaults
const DEFAULT_CTA_FONT_SIZE = 36; // Slightly smaller than caption
const DEFAULT_CTA_Y = 80; // Default vertical position for CTA (lower on image)
const DEFAULT_CTA_COLOR = '#FFD700'; // Gold color for CTA

// Hook Colors (From Image)
const TURQUOISE = '#2acde5';
const RED = '#fa2656';

// Hook Presets
const hookPresets = {
  light: { bgColor: 'rgba(255, 255, 255, 0.95)', textColor: '#000000' },
  dark: { bgColor: 'rgba(0, 0, 0, 0.95)', textColor: '#FFFFFF' }
};

// Helper function to create a new slot
const createNewSlot = (index) => ({
  id: index,
  imageSrc: null,
  imageElement: null,
  caption: '',
  fileName: `image_${index + 1}`,
  fontSize: DEFAULT_FONT_SIZE,
  captionY: DEFAULT_CAPTION_Y,
  urlInputValue: '',
  // CTA related state
  showCTA: false,
  ctaText: '',
  ctaColor: DEFAULT_CTA_COLOR,
  ctaFontSize: DEFAULT_CTA_FONT_SIZE,
  ctaY: DEFAULT_CTA_Y,
});

// Helper function to wrap text (Needed for Hook Overlay, Captions, and CTA)
const wrapText = (ctx, text, maxWidth) => {
  if (!text) return [''];
  const words = text.split(' ');
  const lines = [];
  if (words.length === 0) return [''];
  let currentLine = words[0] || '';
  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    if (!word) continue;
    const testLine = currentLine + " " + word;
    const width = ctx.measureText(testLine).width;
    if (width < maxWidth || currentLine === '') {
      currentLine = testLine;
    } else {
      lines.push(currentLine.trim()); // Trim existing line before pushing
      currentLine = word;
    }
  }
  lines.push(currentLine.trim()); // Push the last line
  return lines.filter(line => line !== ''); // Filter out any empty lines
};


function ImageEditorPage() {
  // --- State Variables ---
  const [message, setMessage] = useState({ text: 'Load images, add captions, CTAs, and adjust styles.', type: 'info' });
  const [textColor, setTextColor] = useState('#FFFFFF');
  const [outlineColor, setOutlineColor] = useState('#000000');
  const [enableOutline, setEnableOutline] = useState(true);
  const [enableShadow, setEnableShadow] = useState(false);
  const [shadowColor, setShadowColor] = useState('#000000');
  const [enableTextBackground, setEnableTextBackground] = useState(false);
  const [textBackgroundColor, setTextBackgroundColor] = useState('#000000');
  const [textBackgroundOpacity, setTextBackgroundOpacity] = useState(70);

  // --- Progress Bar State ---
  const [showProgressBar, setShowProgressBar] = useState(true);
  const [progressBarColor, setProgressBarColor] = useState('#38bdf8');
  const [progressBarHeight, setProgressBarHeight] = useState(10); // Height for final 720px canvas

  // --- Frame Border State ---
  const [enableBorderFrame, setEnableBorderFrame] = useState(false);
  const [borderFrameColor, setBorderFrameColor] = useState('#FFFFFF');
  const [borderFrameThickness, setBorderFrameThickness] = useState(15); // Thickness for final 720px canvas

  // --- Hook Overlay State ---
  const [showHookOverlay, setShowHookOverlay] = useState(false);
  const [hookMode, setHookMode] = useState('dark');
  const [hookVerticalPosition, setHookVerticalPosition] = useState(15);
  const [hookOpacity, setHookOpacity] = useState(95);
  const [hookFontSizeMultiplier, setHookFontSizeMultiplier] = useState(1.2);
  const [hookText, setHookText] = useState('');
  const [showCaption1WithHook, setShowCaption1WithHook] = useState(false);

  // State for image slots data - now dynamic
  const [imageSlotsData, setImageSlotsData] = useState(
    Array(INITIAL_NUM_SLOTS).fill(null).map((_, i) => createNewSlot(i))
  );

  // Refs
  const previewCanvasRefs = useRef([]);
  // Update canvas refs to handle dynamic growth
  previewCanvasRefs.current = imageSlotsData.map(
    (slot, i) => previewCanvasRefs.current[i] ?? React.createRef()
  );
  const bulkFileRef = useRef(null);
  const jsonFileRef = useRef(null);
  const hookOverlayToggleRef = useRef(null);
  const hookVerticalPositionSliderRef = useRef(null);
  const hookOpacitySliderRef = useRef(null);
  const hookFontSizeMultiplierSliderRef = useRef(null);



  // --- Helper Functions ---
  const displayMessage = (text, type = 'info') => {
    setMessage({ text, type });
  };

  const sanitizeFileName = (name) => {
    return name.replace(/\.[^/.]+$/, "").replace(/[^a-zA-Z0-9_-]/g, '_');
  };

  // --- Text Drawing Logic (For Captions & CTA) ---
  const wrapAndDrawText = (context, text, x, y, maxWidth, targetFontSize, minFontSize, lineHeightFactor, styles, isPreview = false) => {
    if (!text || !text.trim() || !context) return;

    let currentFontSize = targetFontSize;
    let lines = [];

    function getLinesForFontSize(fs) {
      context.font = `bold ${fs}px Inter, sans-serif`;
      return wrapText(context, text, maxWidth);
    }

    // This loop reduces font size if text overflows, ensuring it fits within maxWidth
    while (currentFontSize >= minFontSize) {
      context.font = `bold ${currentFontSize}px Inter, sans-serif`;
      lines = getLinesForFontSize(currentFontSize);
      if (lines.every(line => context.measureText(line).width <= maxWidth)) break;
      currentFontSize = Math.max(minFontSize, currentFontSize - (isPreview ? 1 : 2));
      if (currentFontSize <= minFontSize && lines.some(line => context.measureText(line).width > maxWidth)) {
        lines = getLinesForFontSize(minFontSize);
        break;
      }
    }

    context.font = `bold ${currentFontSize}px Inter, sans-serif`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    const lineHeight = currentFontSize * lineHeightFactor;
    const totalTextHeight = lines.length * lineHeight;
    const startY = y - (totalTextHeight / 2) + (lineHeight / 2);

    // --- Draw Text Background ---
    if (styles.enableTextBackground) {
      context.save();
      context.globalAlpha = styles.textBackgroundOpacity / 100;
      context.fillStyle = styles.textBackgroundColor;
      const padding = currentFontSize * 0.2; // Padding based on font size

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const textMetrics = context.measureText(line);
        const lineWidth = textMetrics.width;
        const lineY = startY + (i * lineHeight);

        const bgX = x - (lineWidth / 2) - padding;
        const bgY = lineY - (lineHeight / 2) - padding / 2;
        const bgWidth = lineWidth + (padding * 2);
        const bgHeight = lineHeight + padding;

        context.fillRect(bgX, bgY, bgWidth, bgHeight);
      }
      context.restore();
    }

    // --- Draw Text (Outline and Fill) ---
    context.fillStyle = styles.textColor;
    if (styles.enableOutline) {
      context.strokeStyle = styles.outlineColor;
      context.lineWidth = Math.max(1, Math.floor(currentFontSize * styles.outlineThicknessFactor));
    }

    for (let i = 0; i < lines.length; i++) {
      const lineY = startY + (i * lineHeight);
      if (styles.enableOutline) context.strokeText(lines[i], x, lineY);
      context.fillText(lines[i], x, lineY);
    }
  };

  // --- Hook Overlay Drawing ---
  const drawHookOverlay = useCallback((drawCtx, targetWidth, targetHeight, hookTextToDraw, isPreview = false) => {
    if (!showHookOverlay || !hookTextToDraw || !hookTextToDraw.trim()) return;

    const preset = hookPresets[hookMode];
    const activeScaleFactor = isPreview ? (PREVIEW_CANVAS_WIDTH / FINAL_CANVAS_WIDTH) : 1;
    const baseFontSize = Math.floor(FINAL_CANVAS_WIDTH * 0.065);
    const fontSize = Math.floor(baseFontSize * hookFontSizeMultiplier * activeScaleFactor);
    const lineHeight = fontSize * 1.25;
    drawCtx.font = `bold ${fontSize}px Inter, sans-serif`;
    drawCtx.textAlign = 'center';
    drawCtx.textBaseline = 'middle';

    const maxWidth = targetWidth * 0.85;
    const lines = wrapText(drawCtx, hookTextToDraw, maxWidth);
    const textBlockHeight = lines.length * lineHeight;
    let actualTextWidth = 0;
    lines.forEach(line => {
      const lineWidth = drawCtx.measureText(line).width;
      if (lineWidth > actualTextWidth) actualTextWidth = lineWidth;
    });

    const paddingX = 40 * activeScaleFactor;
    const paddingY = 30 * activeScaleFactor;
    const boxWidth = actualTextWidth + 2 * paddingX;
    const boxHeight = textBlockHeight + 2 * paddingY;
    const boxX = (targetWidth - boxWidth) / 2;
    const offset = 6 * activeScaleFactor;
    const maxBoxY = targetHeight - boxHeight - offset;
    const minBoxY = offset;
    const boxY = minBoxY + ((maxBoxY - minBoxY) * (hookVerticalPosition / 100));

    drawCtx.save();
    drawCtx.globalAlpha = hookOpacity / 100;
    drawCtx.fillStyle = TURQUOISE;
    drawCtx.fillRect(boxX - offset, boxY - offset, boxWidth, boxHeight);
    drawCtx.fillStyle = RED;
    drawCtx.fillRect(boxX + offset, boxY + offset, boxWidth, boxHeight);
    drawCtx.fillStyle = preset.bgColor;
    drawCtx.fillRect(boxX, boxY, boxWidth, boxHeight);
    drawCtx.restore();

    drawCtx.fillStyle = preset.textColor;
    const textX = boxX + boxWidth / 2;
    const textBlockStartY = boxY + (boxHeight - textBlockHeight) / 2;
    lines.forEach((line, index) => {
      drawCtx.fillText(line, textX, textBlockStartY + (index * lineHeight) + (lineHeight / 2));
    });
  }, [showHookOverlay, hookMode, hookFontSizeMultiplier, hookVerticalPosition, hookOpacity]);

  // --- Progress Bar Drawing ---
  const drawProgressBar = (ctx, progress, color, height) => {
    const canvasWidth = ctx.canvas.width;
    const canvasHeight = ctx.canvas.height;
    const barHeight = Math.max(1, height);
    const y = canvasHeight - barHeight;

    ctx.fillStyle = 'rgba(100, 116, 139, 0.5)';
    ctx.fillRect(0, y, canvasWidth, barHeight);

    ctx.fillStyle = color;
    ctx.fillRect(0, y, canvasWidth * progress, barHeight);
  };

  // --- Border Frame Drawing ---
  const drawBorderFrame = (ctx, color, thickness) => {
    if (thickness <= 0) return;
    ctx.strokeStyle = color;
    ctx.lineWidth = thickness;
    ctx.strokeRect(thickness / 2, thickness / 2, ctx.canvas.width - thickness, ctx.canvas.height - thickness);
  };

  // --- Image Loading ---
  const loadImageForSlot = (slotIndex, src, fileName = '') => {
    displayMessage(`Loading image for slot ${slotIndex + 1}...`, 'info');
    const img = new Image();
    if (src.startsWith('http')) {
      img.crossOrigin = "Anonymous";
    }

    img.onload = () => {
      setImageSlotsData(prevSlots => prevSlots.map((slot, idx) => {
        if (idx === slotIndex) {
          const newFileName = fileName ? sanitizeFileName(fileName) : `image_${slotIndex + 1}`;
          const currentCaption = slot.caption;
          const newCaption = (currentCaption.trim() === '' || currentCaption === slot.fileName.replace(/_/g, ' ')) && fileName
            ? newFileName.replace(/_/g, ' ')
            : currentCaption;
          return { ...slot, imageSrc: src, imageElement: img, fileName: newFileName, caption: newCaption, urlInputValue: src.startsWith('http') ? src : '' };
        }
        return slot;
      }));
      displayMessage(`Image for slot ${slotIndex + 1} loaded: ${fileName || 'from URL'}.`, 'success');
    };
    img.onerror = () => {
      setImageSlotsData(prevSlots => prevSlots.map((slot, idx) =>
        idx === slotIndex ? { ...slot, imageSrc: null, imageElement: null, urlInputValue: slot.urlInputValue } : slot
      ));
      displayMessage(`Error loading image for slot ${slotIndex + 1}. Check URL or file.`, 'error');
    };
    img.src = src;
  };

  // useEffect to render previews when data or styles change
  useEffect(() => {
    const activeSlots = imageSlotsData.filter(s => s.imageSrc);
    const totalActiveSlots = activeSlots.length;
    const scaleFactor = PREVIEW_CANVAS_WIDTH / FINAL_CANVAS_WIDTH;

    imageSlotsData.forEach((slotData, slotIndex) => {
      const canvas = previewCanvasRefs.current[slotIndex]?.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, PREVIEW_CANVAS_WIDTH, PREVIEW_CANVAS_HEIGHT);

        // Draw Image
        if (slotData.imageElement && slotData.imageElement.complete && slotData.imageElement.naturalWidth > 0) {
          const img = slotData.imageElement;
          const imgWidth = img.naturalWidth, imgHeight = img.naturalHeight;
          const canvasAspectRatio = PREVIEW_CANVAS_WIDTH / PREVIEW_CANVAS_HEIGHT;
          let sourceX = 0, sourceY = 0, sourceWidth = imgWidth, sourceHeight = imgHeight;
          const imageAspectRatio = imgWidth / imgHeight;
          if (imageAspectRatio > canvasAspectRatio) {
            sourceWidth = imgHeight * canvasAspectRatio; sourceX = (imgWidth - sourceWidth) / 2;
          } else if (imageAspectRatio < canvasAspectRatio) {
            sourceHeight = imgWidth / canvasAspectRatio; sourceY = (imgHeight - sourceHeight) / 2;
          }
          ctx.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, PREVIEW_CANVAS_WIDTH, PREVIEW_CANVAS_HEIGHT);
        } else {
          ctx.fillStyle = '#E5E7EB'; ctx.fillRect(0, 0, PREVIEW_CANVAS_WIDTH, PREVIEW_CANVAS_HEIGHT);
          ctx.fillStyle = '#6B7280'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
          const placeholderFontSize = Math.floor(PREVIEW_CANVAS_WIDTH / 12);
          ctx.font = `${placeholderFontSize}px Inter, sans-serif`;
          ctx.fillText('No Image', PREVIEW_CANVAS_WIDTH / 2, PREVIEW_CANVAS_HEIGHT / 2);
        }

        // Draw Border Frame
        if (enableBorderFrame) {
          const scaledThickness = borderFrameThickness * scaleFactor;
          drawBorderFrame(ctx, borderFrameColor, scaledThickness);
        }

        // Apply global shadow if enabled
        if (enableShadow) {
          ctx.shadowColor = shadowColor;
          ctx.shadowOffsetX = PREVIEW_CANVAS_WIDTH * 0.007;
          ctx.shadowOffsetY = PREVIEW_CANVAS_WIDTH * 0.007;
          ctx.shadowBlur = PREVIEW_CANVAS_WIDTH * 0.01;
        } else {
          ctx.shadowColor = 'transparent'; ctx.shadowOffsetX = 0; ctx.shadowOffsetY = 0; ctx.shadowBlur = 0;
        }

        // Draw Caption
        const captionText = slotData.caption;
        const shouldDrawNormalCaption = captionText && (
          slotIndex !== 0 || (slotIndex === 0 && !showHookOverlay) || (slotIndex === 0 && showHookOverlay && showCaption1WithHook)
        );
        if (shouldDrawNormalCaption) {
          // FIX: Removed the Math.max(12, ...) to allow for proper proportional scaling on the preview.
          const scaledFontSize = slotData.fontSize * scaleFactor;
          const scaledMinFontSize = 4; // Use a small fixed minimum for previews
          const captionStyles = {
            textColor: textColor,
            enableOutline: enableOutline,
            outlineColor: outlineColor,
            outlineThicknessFactor: 1 / 20,
            enableTextBackground: enableTextBackground,
            textBackgroundColor: textBackgroundColor,
            textBackgroundOpacity: textBackgroundOpacity
          };
          const textMaxWidth = PREVIEW_CANVAS_WIDTH * 0.9;
          const captionCenterY = PREVIEW_CANVAS_HEIGHT * (slotData.captionY / 100);
          wrapAndDrawText(ctx, captionText, PREVIEW_CANVAS_WIDTH / 2, captionCenterY,
            textMaxWidth, scaledFontSize, scaledMinFontSize, 1.2, captionStyles, true);
        }

        // Draw CTA Text
        if (slotData.showCTA && slotData.ctaText) {
          // FIX: Removed the Math.max(10, ...) for the same reason as above.
          const ctaScaledFontSize = slotData.ctaFontSize * scaleFactor;
          const ctaScaledMinFontSize = 4; // Use a small fixed minimum for previews
          const ctaStyles = {
            textColor: slotData.ctaColor,
            enableOutline: enableOutline,
            outlineColor: outlineColor,
            outlineThicknessFactor: 1 / 20,
            enableTextBackground: enableTextBackground,
            textBackgroundColor: textBackgroundColor,
            textBackgroundOpacity: textBackgroundOpacity
          };
          const ctaTextMaxWidth = PREVIEW_CANVAS_WIDTH * 0.85;
          const ctaCenterY = PREVIEW_CANVAS_HEIGHT * (slotData.ctaY / 100);

          wrapAndDrawText(ctx, slotData.ctaText, PREVIEW_CANVAS_WIDTH / 2, ctaCenterY,
            ctaTextMaxWidth, ctaScaledFontSize, ctaScaledMinFontSize, 1.2, ctaStyles, true);
        }

        ctx.shadowColor = 'transparent';

        // Draw Hook Overlay for Slot 1
        if (slotIndex === 0 && showHookOverlay && hookText) {
          drawHookOverlay(ctx, PREVIEW_CANVAS_WIDTH, PREVIEW_CANVAS_HEIGHT, hookText, true);
        }

        // Draw Progress Bar on Preview
        if (showProgressBar && totalActiveSlots > 1 && slotData.imageSrc) {
          const currentActiveIndex = activeSlots.findIndex(s => s.id === slotData.id);
          if (currentActiveIndex !== -1) {
            const progress = (currentActiveIndex + 1) / totalActiveSlots;
            const scaledHeight = progressBarHeight * scaleFactor;
            drawProgressBar(ctx, progress, progressBarColor, scaledHeight);
          }
        }
      }
    });
  }, [imageSlotsData, textColor, outlineColor, enableOutline, enableShadow, shadowColor,
    showHookOverlay, hookMode, hookVerticalPosition, hookOpacity, hookFontSizeMultiplier, hookText, showCaption1WithHook,
    enableTextBackground, textBackgroundColor, textBackgroundOpacity,
    showProgressBar, progressBarColor, progressBarHeight,
    enableBorderFrame, borderFrameColor, borderFrameThickness,
    drawHookOverlay
  ]);


  // --- Event Handlers ---
  const handleFileChange = (slotIndex, event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 15 * 1024 * 1024) {
        displayMessage(`File for image ${slotIndex + 1} too large (max 15MB).`, 'error');
        event.target.value = ''; return;
      }
      const reader = new FileReader();
      reader.onload = (e) => loadImageForSlot(slotIndex, e.target.result, file.name);
      reader.onerror = () => displayMessage(`Error reading file for image ${slotIndex + 1}.`, 'error');
      reader.readAsDataURL(file);
    }
    event.target.value = '';
  };

  const handleBulkFiles = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      displayMessage('No files selected for bulk upload.', 'info');
      return;
    }

    displayMessage(`Starting bulk upload of ${files.length} files...`, 'info');

    // Check if we need to add more slots
    const emptySlots = imageSlotsData.filter(slot => !slot.imageSrc).length;
    const additionalSlotsNeeded = Math.max(0, files.length - emptySlots);

    // Update state if we need more slots
    if (additionalSlotsNeeded > 0) {
      displayMessage(`Adding ${additionalSlotsNeeded} new slots for additional images...`, 'info');
      setImageSlotsData(prevSlots => {
        const currentLength = prevSlots.length;
        const newSlots = Array(additionalSlotsNeeded).fill(null).map((_, i) => createNewSlot(currentLength + i));
        return [...prevSlots, ...newSlots];
      });

      // Wait a moment for state to update
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Now process the files
    const results = await processFiles(files, additionalSlotsNeeded > 0 ?
      [...imageSlotsData, ...Array(additionalSlotsNeeded).fill(null).map((_, i) => createNewSlot(imageSlotsData.length + i))] :
      imageSlotsData
    );

    let finalMessage = `Bulk upload finished. Loaded: ${results.loaded}.`;
    if (results.skipped > 0) finalMessage += ` Skipped: ${results.skipped}.`;
    displayMessage(finalMessage, results.skipped > 0 && results.loaded === 0 ? 'error' : results.loaded > 0 ? 'success' : 'info');

    if (bulkFileRef.current) bulkFileRef.current.value = '';
  };

  const processFiles = async (files, currentSlots) => {
    const loadPromises = [];
    let currentSlotIndex = 0;
    let filesLoadedCount = 0;
    let filesSkippedCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      let foundEmptySlot = false, targetSlot = -1;

      // Find the next empty slot
      for (let j = 0; j < currentSlots.length; j++) {
        const checkIndex = (currentSlotIndex + j) % currentSlots.length;
        if (!currentSlots[checkIndex].imageSrc) {
          targetSlot = checkIndex;
          foundEmptySlot = true;
          currentSlotIndex = (targetSlot + 1) % currentSlots.length;
          break;
        }
      }

      if (!foundEmptySlot) {
        filesSkippedCount++;
        displayMessage(`No empty slot for "${file.name}".`, 'error');
        continue;
      }

      if (file.size > 15 * 1024 * 1024) {
        displayMessage(`Skipping "${file.name}" (slot ${targetSlot + 1}): too large.`, 'error');
        filesSkippedCount++;
        continue;
      }

      loadPromises.push(new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          loadImageForSlot(targetSlot, e.target.result, file.name);
          resolve();
        };
        reader.onerror = () => {
          displayMessage(`Error reading "${file.name}".`, 'error');
          reject();
        };
        reader.readAsDataURL(file);
      }));
    }

    const results = await Promise.allSettled(loadPromises);

    results.forEach(result => {
      if (result.status === 'fulfilled') {
        filesLoadedCount++;
      } else {
        filesSkippedCount++;
      }
    });

    return { loaded: filesLoadedCount, skipped: filesSkippedCount };
  };

  const handleUrlInputChange = (slotIndex, value) => {
    setImageSlotsData(prevSlots => prevSlots.map((slot, idx) =>
      idx === slotIndex ? { ...slot, urlInputValue: value } : slot
    ));
  };

  const handleLoadFromUrl = (slotIndex) => {
    const url = imageSlotsData[slotIndex].urlInputValue.trim();
    if (url) loadImageForSlot(slotIndex, url, url.substring(url.lastIndexOf('/') + 1));
    else displayMessage(`Enter URL for image ${slotIndex + 1}.`, 'error');
  };

  const handleCaptionChange = (slotIndex, value) => {
    setImageSlotsData(prevSlots => prevSlots.map((slot, idx) =>
      idx === slotIndex ? { ...slot, caption: value } : slot
    ));
  };

  const handleFontSizeChange = (slotIndex, value) => {
    setImageSlotsData(prevSlots => prevSlots.map((slot, idx) =>
      idx === slotIndex ? { ...slot, fontSize: parseInt(value) } : slot
    ));
  };

  const handleCaptionYChange = (slotIndex, value) => {
    setImageSlotsData(prevSlots => prevSlots.map((slot, idx) =>
      idx === slotIndex ? { ...slot, captionY: parseInt(value) } : slot
    ));
  };

  // --- CTA Event Handlers ---
  const handleCtaToggle = (slotIndex) => {
    setImageSlotsData(prevSlots => prevSlots.map((slot, idx) =>
      idx === slotIndex ? { ...slot, showCTA: !slot.showCTA } : slot
    ));
  };

  const handleCtaTextChange = (slotIndex, value) => {
    setImageSlotsData(prevSlots => prevSlots.map((slot, idx) =>
      idx === slotIndex ? { ...slot, ctaText: value } : slot
    ));
  };

  const handleCtaColorChange = (slotIndex, value) => {
    setImageSlotsData(prevSlots => prevSlots.map((slot, idx) =>
      idx === slotIndex ? { ...slot, ctaColor: value } : slot
    ));
  };

  const handleCtaFontSizeChange = (slotIndex, value) => {
    setImageSlotsData(prevSlots => prevSlots.map((slot, idx) =>
      idx === slotIndex ? { ...slot, ctaFontSize: parseInt(value) } : slot
    ));
  };

  const handleCtaYChange = (slotIndex, value) => {
    setImageSlotsData(prevSlots => prevSlots.map((slot, idx) =>
      idx === slotIndex ? { ...slot, ctaY: parseInt(value) } : slot
    ));
  };

  const handleJsonUpload = (event) => {
    const file = event.target.files[0];
    if (!file) { displayMessage('No JSON file selected.', 'info'); return; }
    if (file.type !== 'application/json') { displayMessage('Invalid file type. Upload JSON.', 'error'); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonContent = JSON.parse(e.target.result);

        // Find the highest caption number in the JSON
        const captionKeys = Object.keys(jsonContent).filter(key => key.startsWith('caption'));
        const maxCaptionIndex = Math.max(0, ...captionKeys.map(key => {
          const match = key.match(/caption(\d+)/);
          return match ? parseInt(match[1]) : 0;
        }));

        // Add new slots if needed
        const slotsNeeded = maxCaptionIndex;
        const currentSlotCount = imageSlotsData.length;

        if (slotsNeeded > currentSlotCount) {
          const additionalSlots = slotsNeeded - currentSlotCount;
          displayMessage(`Adding ${additionalSlots} new slots for JSON captions...`, 'info');
          setImageSlotsData(prevSlots => {
            const newSlots = Array(additionalSlots).fill(null).map((_, i) => createNewSlot(currentSlotCount + i));
            return [...prevSlots, ...newSlots];
          });

          // Wait for state update
          setTimeout(() => {
            updateCaptionsFromJson(jsonContent, slotsNeeded);
          }, 100);
        } else {
          updateCaptionsFromJson(jsonContent, currentSlotCount);
        }

      } catch (error) { displayMessage('Error parsing JSON file.', 'error'); console.error('JSON parsing error:', error); }
    };
    reader.onerror = () => displayMessage('Error reading JSON file.', 'error');
    reader.readAsText(file);
    if (jsonFileRef.current) jsonFileRef.current.value = '';
  };

  const updateCaptionsFromJson = (jsonContent, totalSlots) => {
    let captionsUpdated = 0, hookUpdated = false;

    setImageSlotsData(prevSlots => prevSlots.map((slot, index) => {
      const captionKey = `caption${index + 1}`;
      let updatedSlot = { ...slot };
      if (jsonContent.hasOwnProperty(captionKey) && typeof jsonContent[captionKey] === 'string') {
        updatedSlot.caption = jsonContent[captionKey];
        captionsUpdated++;
      }
      return updatedSlot;
    }));

    if (jsonContent.hasOwnProperty('hook_text') && typeof jsonContent['hook_text'] === 'string') {
      setHookText(jsonContent['hook_text']);
      hookUpdated = true;
    }

    let message = `Captions updated for ${captionsUpdated} slots.`;
    if (hookUpdated) message += ' Hook text updated.';
    displayMessage(message, 'success');
  };

  // --- Download All Logic ---
  const handleDownloadAll = async () => {
    if (typeof JSZip === 'undefined' || typeof saveAs === 'undefined') {
      displayMessage('Error: JSZip/FileSaver not found.', 'error'); return;
    }
    displayMessage('Starting processing...', 'info');
    const downloadButton = document.getElementById('downloadAllButtonReact');
    if (downloadButton) downloadButton.disabled = true;

    const zip = new JSZip();
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = FINAL_CANVAS_WIDTH; tempCanvas.height = FINAL_CANVAS_HEIGHT;
    const tempCtx = tempCanvas.getContext('2d');

    const slotsToProcess = imageSlotsData.filter(slot => slot.imageElement && slot.imageElement.complete && slot.imageElement.naturalWidth > 0);
    const totalToProcess = slotsToProcess.length;
    let processedImageCount = 0;

    for (let i = 0; i < imageSlotsData.length; i++) {
      const slotData = imageSlotsData[i];
      if (slotData.imageElement && slotData.imageElement.complete && slotData.imageElement.naturalWidth > 0) {
        processedImageCount++;
        tempCtx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
        const img = slotData.imageElement;
        const imgWidth = img.naturalWidth, imgHeight = img.naturalHeight;
        const canvasAspectRatio = tempCanvas.width / tempCanvas.height;
        let sourceX = 0, sourceY = 0, sourceWidth = imgWidth, sourceHeight = imgHeight;
        const imageAspectRatio = imgWidth / imgHeight;
        if (imageAspectRatio > canvasAspectRatio) {
          sourceWidth = imgHeight * canvasAspectRatio; sourceX = (imgWidth - sourceWidth) / 2;
        } else if (imageAspectRatio < canvasAspectRatio) {
          sourceHeight = imgWidth / canvasAspectRatio; sourceY = (imgHeight - sourceHeight) / 2;
        }
        tempCtx.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, tempCanvas.width, tempCanvas.height);

        if (enableBorderFrame) {
          drawBorderFrame(tempCtx, borderFrameColor, borderFrameThickness);
        }

        if (enableShadow) {
          tempCtx.shadowColor = shadowColor;
          tempCtx.shadowOffsetX = tempCanvas.width * 0.007; tempCtx.shadowOffsetY = tempCanvas.width * 0.007;
          tempCtx.shadowBlur = tempCanvas.width * 0.01;
        } else {
          tempCtx.shadowColor = 'transparent'; tempCtx.shadowOffsetX = 0; tempCtx.shadowOffsetY = 0; tempCtx.shadowBlur = 0;
        }

        const captionText = slotData.caption;
        const shouldDrawNormalCaption = captionText && (
          i !== 0 || (i === 0 && !showHookOverlay) || (i === 0 && showHookOverlay && showCaption1WithHook)
        );
        if (shouldDrawNormalCaption) {
          const captionStyles = {
            textColor: textColor, enableOutline: enableOutline,
            outlineColor: outlineColor, outlineThicknessFactor: 1 / 18,
            enableTextBackground: enableTextBackground,
            textBackgroundColor: textBackgroundColor,
            textBackgroundOpacity: textBackgroundOpacity
          };
          const targetFontSize = slotData.fontSize;
          const minFontSize = Math.max(10, targetFontSize / 3);
          const textMaxWidth = tempCanvas.width * 0.9;
          const captionCenterY = tempCanvas.height * (slotData.captionY / 100);
          wrapAndDrawText(tempCtx, captionText, tempCanvas.width / 2, captionCenterY,
            textMaxWidth, targetFontSize, minFontSize, 1.2, captionStyles, false);
        }

        if (slotData.showCTA && slotData.ctaText) {
          const ctaStyles = {
            textColor: slotData.ctaColor,
            enableOutline: enableOutline,
            outlineColor: outlineColor,
            outlineThicknessFactor: 1 / 18,
            enableTextBackground: enableTextBackground,
            textBackgroundColor: textBackgroundColor,
            textBackgroundOpacity: textBackgroundOpacity
          };
          const ctaTargetFontSize = slotData.ctaFontSize;
          const ctaMinFontSize = Math.max(8, ctaTargetFontSize / 3);
          const ctaTextMaxWidth = tempCanvas.width * 0.85;
          const ctaCenterY = tempCanvas.height * (slotData.ctaY / 100);
          wrapAndDrawText(tempCtx, slotData.ctaText, tempCanvas.width / 2, ctaCenterY,
            ctaTextMaxWidth, ctaTargetFontSize, ctaMinFontSize, 1.2, ctaStyles, false);
        }

        tempCtx.shadowColor = 'transparent';

        if (i === 0 && showHookOverlay && hookText) {
          drawHookOverlay(tempCtx, FINAL_CANVAS_WIDTH, FINAL_CANVAS_HEIGHT, hookText, false);
        }

        if (showProgressBar && totalToProcess > 1) {
          const progress = processedImageCount / totalToProcess;
          drawProgressBar(tempCtx, progress, progressBarColor, progressBarHeight);
        }

        try {
          const dataUrl = tempCanvas.toDataURL('image/png');
          zip.file(`${slotData.fileName || ('image_' + (i + 1))}.png`, dataUrl.substring(dataUrl.indexOf(',') + 1), { base64: true });
          displayMessage(`Processed image ${i + 1}... (${processedImageCount} of ${totalToProcess})`, 'info');
        } catch (e) { console.error(`Error processing image ${i + 1}:`, e); displayMessage(`Error processing image ${i + 1}.`, 'error'); }
      } else if (slotData.imageSrc) { displayMessage(`Skipping image ${i + 1} (not loaded).`, 'error'); }
    }

    if (processedImageCount > 0) {
      displayMessage(`Generating ZIP with ${processedImageCount} images...`, 'info');
      zip.generateAsync({ type: "blob" })
        .then(content => { saveAs(content, "processed_social_images.zip"); displayMessage(`${processedImageCount} images processed!`, 'success'); })
        .catch(err => { console.error("ZIP Error:", err); displayMessage('Error generating ZIP.', 'error'); });
    } else { displayMessage('No images processed.', 'error'); }
    if (downloadButton) downloadButton.disabled = false;
  };

  // --- JSX Structure ---
  const commonButtonClass = "w-full font-semibold py-2.5 px-4 rounded-lg transition duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-opacity-75 disabled:opacity-50 disabled:cursor-not-allowed";
  const skyButtonClass = `${commonButtonClass} bg-sky-600 hover:bg-sky-700 text-white focus:ring-sky-500 focus:ring-offset-2 focus:ring-offset-slate-800`;
  const greenButtonClass = `${commonButtonClass} bg-green-600 hover:bg-green-700 text-white focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-slate-900`;
  const fileInputLabelClass = "block w-full text-center bg-slate-600 hover:bg-slate-500 text-white py-2 px-3 rounded-md cursor-pointer text-sm";
  const inputBaseClass = "w-full p-2 border border-slate-600 rounded-md shadow-sm focus:ring-sky-500 focus:border-sky-500 text-sm bg-slate-700 text-gray-200 placeholder-gray-400";
  const labelClass = "block text-sm font-medium text-gray-300 mb-1";
  const rangeInputClass = "w-full h-2 bg-slate-600 rounded-lg appearance-none cursor-pointer accent-sky-500";
  const checkboxLabelClass = "flex items-center gap-2 text-sm text-gray-300 cursor-pointer";
  const checkboxClass = "h-4 w-4 text-sky-500 border-slate-500 rounded focus:ring-sky-400 bg-slate-600";
  const presetButtonClass = `${commonButtonClass} bg-slate-600 hover:bg-slate-500 text-gray-200 text-xs py-2`;


  return (
    <div className="bg-gray-800/30 p-4 sm:p-6 rounded-xl shadow-2xl w-full max-w-7xl mx-auto my-8">
      <h1 className="text-2xl sm:text-3xl font-bold text-center text-sky-300 mb-6">9:16 Image Batch Tool</h1>

      <div className={`mb-4 text-sm p-3 rounded-md text-center
        ${message.type === 'error' ? 'bg-red-700/50 text-red-200' :
          message.type === 'success' ? 'bg-green-700/50 text-green-200' :
            'bg-sky-700/50 text-sky-200'}`}
      >
        {message.text}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="p-4 border border-slate-700 rounded-lg space-y-3 bg-slate-700/30">
          <h3 className="text-md font-semibold text-sky-400 mb-3 text-center">Global Text Styling</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="textColorReact" className="block text-xs font-medium text-gray-300 mb-1">Caption Text Color:</label>
              <input type="color" id="textColorReact" value={textColor} onChange={(e) => setTextColor(e.target.value)} title="Caption Text Color" className="w-full h-10 p-1 border border-slate-600 rounded-md cursor-pointer bg-slate-700" />
            </div>
            <div>
              <label htmlFor="outlineColorReact" className="block text-xs font-medium text-gray-300 mb-1">Outline Color (Global):</label>
              <input type="color" id="outlineColorReact" value={outlineColor} onChange={(e) => setOutlineColor(e.target.value)} title="Outline Color" className="w-full h-10 p-1 border border-slate-600 rounded-md cursor-pointer bg-slate-700" />
            </div>
          </div>
          <div className="flex items-center justify-around mt-3 pt-3 border-t border-slate-600">
            <div className={checkboxLabelClass}>
              <input type="checkbox" id="enableOutlineReact" checked={enableOutline} onChange={(e) => setEnableOutline(e.target.checked)} className={checkboxClass} />
              <label htmlFor="enableOutlineReact">Enable Outline</label>
            </div>
            <div className={checkboxLabelClass}>
              <input type="checkbox" id="enableShadowReact" checked={enableShadow} onChange={(e) => setEnableShadow(e.target.checked)} className={checkboxClass} />
              <label htmlFor="enableShadowReact">Enable Shadow</label>
            </div>
            <div className={checkboxLabelClass}>
              <input type="checkbox" id="enableTextBackground" checked={enableTextBackground} onChange={(e) => setEnableTextBackground(e.target.checked)} className={checkboxClass} />
              <label htmlFor="enableTextBackground">Enable Text BG</label>
            </div>
          </div>
          {enableShadow && (
            <div className="mt-3 pt-3 border-t border-slate-600">
              <label htmlFor="shadowColorReact" className="block text-xs font-medium text-gray-300 mb-1">Shadow Color (Global):</label>
              <input type="color" id="shadowColorReact" value={shadowColor} onChange={(e) => setShadowColor(e.target.value)} title="Shadow Color" className="w-full h-10 p-1 border border-slate-600 rounded-md cursor-pointer bg-slate-700" />
            </div>
          )}
          {enableTextBackground && (
            <div className="mt-3 pt-3 border-t border-slate-600 space-y-3">
              <div>
                <label htmlFor="textBackgroundColor" className="block text-xs font-medium text-gray-300 mb-1">Background Color:</label>
                <input type="color" id="textBackgroundColor" value={textBackgroundColor} onChange={(e) => setTextBackgroundColor(e.target.value)} title="Text Background Color" className="w-full h-10 p-1 border border-slate-600 rounded-md cursor-pointer bg-slate-700" />
              </div>
              <div>
                <label htmlFor="textBackgroundOpacity" className={`${labelClass} text-xs`}>Background Opacity: {textBackgroundOpacity}%</label>
                <input type="range" id="textBackgroundOpacity" min="10" max="100" value={textBackgroundOpacity} onChange={(e) => setTextBackgroundOpacity(parseInt(e.target.value))} className={rangeInputClass} />
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border border-slate-700 rounded-lg space-y-3 bg-slate-700/30">
          <h3 className="text-md font-semibold text-sky-400 mb-3 text-center">Hook Overlay Styling (Image 1 Only)</h3>
          <label className={checkboxLabelClass}>
            <input type="checkbox" ref={hookOverlayToggleRef} checked={showHookOverlay} onChange={(e) => setShowHookOverlay(e.target.checked)} className={checkboxClass} />
            Enable Hook Overlay for Image 1
          </label>
          {showHookOverlay && (<>
            <div className="flex gap-2">
              <button onClick={() => setHookMode('light')} className={`${presetButtonClass} flex-1 ${hookMode === 'light' ? 'bg-sky-500' : ''}`}>☀️ Light</button>
              <button onClick={() => setHookMode('dark')} className={`${presetButtonClass} flex-1 ${hookMode === 'dark' ? 'bg-sky-500' : ''}`}>🌙 Dark</button>
            </div>
            <div>
              <label htmlFor="hookVerticalPosition" className={`${labelClass} text-xs`}>V-Position: {hookVerticalPosition}%</label>
              <input type="range" id="hookVerticalPosition" ref={hookVerticalPositionSliderRef} min="0" max="100" value={hookVerticalPosition} onChange={(e) => setHookVerticalPosition(parseInt(e.target.value))} className={rangeInputClass} />
            </div>
            <div>
              <label htmlFor="hookOpacity" className={`${labelClass} text-xs`}>Opacity: {hookOpacity}%</label>
              <input type="range" id="hookOpacity" ref={hookOpacitySliderRef} min="10" max="100" value={hookOpacity} onChange={(e) => setHookOpacity(parseInt(e.target.value))} className={rangeInputClass} />
            </div>
            <div>
              <label htmlFor="hookFontSizeMultiplier" className={`${labelClass} text-xs`}>Font Scale: {Math.round(hookFontSizeMultiplier * 100)}%</label>
              <input type="range" id="hookFontSizeMultiplier" ref={hookFontSizeMultiplierSliderRef} min="50" max="250" value={hookFontSizeMultiplier * 100} onChange={(e) => setHookFontSizeMultiplier(parseFloat(e.target.value) / 100)} className={rangeInputClass} />
            </div>
            <p className="text-xs text-gray-400 mt-2">Uses the <span className="font-bold text-sky-300">"Hook Text"</span> field from Image 1.</p>
          </>)}
        </div>
      </div>

      <div className="mb-6 p-4 border border-slate-700 rounded-lg bg-slate-700/30">
        <h3 className="text-md font-semibold text-sky-400 mb-3 text-center">Global Visuals</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-300 text-center">Progress Bar</h4>
            <div className="flex justify-between items-center">
              <label className={checkboxLabelClass}>
                <input type="checkbox" id="showProgressBarReact" checked={showProgressBar} onChange={(e) => setShowProgressBar(e.target.checked)} className={checkboxClass} />
                Show Progress Bar
              </label>
              <input type="color" value={progressBarColor} onChange={(e) => setProgressBarColor(e.target.value)} disabled={!showProgressBar} className="w-12 h-8 p-1 border border-slate-600 rounded-md cursor-pointer bg-slate-700 disabled:opacity-50" title="Progress Bar Color" />
            </div>
            <div>
              <label htmlFor="progressBarHeightReact" className="block text-xs font-medium text-gray-300 mb-0.5">Height: <span className="text-sky-400">{progressBarHeight}px</span></label>
              <input type="range" id="progressBarHeightReact" min="2" max="50" value={progressBarHeight} onChange={(e) => setProgressBarHeight(parseInt(e.target.value))} disabled={!showProgressBar} className={rangeInputClass} />
            </div>
          </div>
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-300 text-center">Frame Border</h4>
            <div className="flex justify-between items-center">
              <label className={checkboxLabelClass}>
                <input type="checkbox" id="enableBorderFrameReact" checked={enableBorderFrame} onChange={(e) => setEnableBorderFrame(e.target.checked)} className={checkboxClass} />
                Enable Frame Border
              </label>
              <input type="color" value={borderFrameColor} onChange={(e) => setBorderFrameColor(e.target.value)} disabled={!enableBorderFrame} className="w-12 h-8 p-1 border border-slate-600 rounded-md cursor-pointer bg-slate-700 disabled:opacity-50" title="Frame Border Color" />
            </div>
            <div>
              <label htmlFor="borderFrameThicknessReact" className="block text-xs font-medium text-gray-300 mb-0.5">Thickness: <span className="text-sky-400">{borderFrameThickness}px</span></label>
              <input type="range" id="borderFrameThicknessReact" min="2" max="100" value={borderFrameThickness} onChange={(e) => setBorderFrameThickness(parseInt(e.target.value))} disabled={!enableBorderFrame} className={rangeInputClass} />
            </div>
          </div>
        </div>
      </div>

      <div className="mb-6 p-4 border border-slate-700 rounded-lg bg-slate-700/30">
        <h3 className="text-md font-semibold text-sky-400 mb-3 text-center">Bulk Upload Options</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="bulkFileReact" className={fileInputLabelClass}>Select Multiple Image Files</label>
            <input type="file" id="bulkFileReact" ref={bulkFileRef} multiple accept="image/*" onChange={handleBulkFiles} className="hidden" />
            <p className="text-xs text-gray-400 mt-2 text-center">Fill empty slots. Max 15MB/file.</p>
          </div>
          <div>
            <label htmlFor="jsonCaptionFile" className={fileInputLabelClass}>Upload Captions/Hook (JSON)</label>
            <input type="file" id="jsonCaptionFile" ref={jsonFileRef} accept="application/json" onChange={handleJsonUpload} className="hidden" />
            <p className="text-xs text-gray-400 mt-2 text-center">JSON: {`{"caption1":"Text", ...}`}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
        {imageSlotsData.map((slot, index) => (
          <div key={slot.id} className="border border-slate-700 rounded-lg p-3 bg-slate-700/30 flex flex-col items-center space-y-2">
            <h4 className="font-semibold text-sky-400 w-full text-left text-sm">Image {index + 1}</h4>
            <div>
              <label className={`${fileInputLabelClass} py-1.5`}>
                <span>Select File</span>
                <input type="file" className="hidden" accept="image/*" onChange={(e) => handleFileChange(index, e)} />
              </label>
            </div>

            <canvas
              ref={previewCanvasRefs.current[index]}
              width={PREVIEW_CANVAS_WIDTH}
              height={PREVIEW_CANVAS_HEIGHT}
              className="border border-slate-600 rounded bg-slate-500"
              style={{ width: `${PREVIEW_CANVAS_WIDTH}px`, height: `${PREVIEW_CANVAS_HEIGHT}px` }}
            ></canvas>

            <div className="relative flex items-center w-full">
              <div className="flex-grow border-t border-slate-600"></div>
              <span className="flex-shrink mx-2 text-gray-400 text-xs">OR</span>
              <div className="flex-grow border-t border-slate-600"></div>
            </div>

            <div className="w-full space-y-1.5">
              <input type="url" placeholder="Load from URL" value={slot.urlInputValue} onChange={(e) => handleUrlInputChange(index, e.target.value)} className={`${inputBaseClass} p-1.5`} />
              <button onClick={() => handleLoadFromUrl(index)} className={`${skyButtonClass} py-1.5 text-sm`}>Load URL</button>
            </div>

            {index === 0 && (
              <input type="text" placeholder="Hook Text (Image 1 Only)" value={hookText} onChange={(e) => setHookText(e.target.value)} className={`${inputBaseClass} p-1.5 bg-sky-900/40 placeholder-sky-300/60`} />
            )}

            <input type="text" placeholder="Caption Text" value={slot.caption} onChange={(e) => handleCaptionChange(index, e.target.value)} className={`${inputBaseClass} p-1.5`} />

            {index === 0 && showHookOverlay && (
              <div className="w-full text-center">
                <label className={checkboxLabelClass + ' justify-center'}>
                  <input type="checkbox" checked={showCaption1WithHook} onChange={(e) => setShowCaption1WithHook(e.target.checked)} className={checkboxClass} />
                  Show Caption 1 with Hook?
                </label>
              </div>
            )}

            <div className="w-full">
              <label htmlFor={`fontSizeInputReact-${index}`} className="block text-xs font-medium text-gray-300 mb-0.5">Caption Font: <span className="text-sky-400">{slot.fontSize}px</span></label>
              <input type="range" id={`fontSizeInputReact-${index}`} min="10" max="100" value={slot.fontSize} onChange={(e) => handleFontSizeChange(index, e.target.value)} className={rangeInputClass} />
            </div>

            <div className="w-full">
              <label htmlFor={`captionYInputReact-${index}`} className="block text-xs font-medium text-gray-300 mb-0.5">Caption V-Pos: <span className="text-sky-400">{slot.captionY}%</span></label>
              <input type="range" id={`captionYInputReact-${index}`} min="5" max="95" value={slot.captionY} onChange={(e) => handleCaptionYChange(index, e.target.value)} className={rangeInputClass} />
            </div>

            {/* --- CTA Controls --- */}
            <div className="w-full pt-2 mt-2 border-t border-slate-600 space-y-2">
              <label className={`${checkboxLabelClass} justify-start`}>
                <input type="checkbox" checked={slot.showCTA} onChange={() => handleCtaToggle(index)} className={checkboxClass} />
                Enable CTA
              </label>

              {slot.showCTA && (
                <div className="p-2 border border-slate-500 rounded-md space-y-2 bg-slate-600/40">
                  <h5 className="text-xs font-semibold text-amber-300">CTA Settings:</h5>
                  <input
                    type="text"
                    placeholder="CTA Text"
                    value={slot.ctaText}
                    onChange={(e) => handleCtaTextChange(index, e.target.value)}
                    className={`${inputBaseClass} p-1.5 text-sm`}
                  />
                  <div>
                    <label htmlFor={`ctaColor-${index}`} className="block text-xs font-medium text-gray-300 mb-0.5">CTA Color:</label>
                    <input
                      type="color"
                      id={`ctaColor-${index}`}
                      value={slot.ctaColor}
                      onChange={(e) => handleCtaColorChange(index, e.target.value)}
                      title="CTA Text Color"
                      className="w-full h-8 p-1 border border-slate-500 rounded-md cursor-pointer bg-slate-700"
                    />
                  </div>
                  <div>
                    <label htmlFor={`ctaFontSize-${index}`} className="block text-xs font-medium text-gray-300 mb-0.5">
                      CTA Font Size: <span className="text-amber-400">{slot.ctaFontSize}px</span>
                    </label>
                    <input
                      type="range"
                      id={`ctaFontSize-${index}`}
                      min="10" max="80"
                      value={slot.ctaFontSize}
                      onChange={(e) => handleCtaFontSizeChange(index, e.target.value)}
                      className={rangeInputClass + " accent-amber-500"}
                    />
                  </div>
                  <div>
                    <label htmlFor={`ctaY-${index}`} className="block text-xs font-medium text-gray-300 mb-0.5">
                      CTA V-Position: <span className="text-amber-400">{slot.ctaY}%</span>
                    </label>
                    <input
                      type="range"
                      id={`ctaY-${index}`}
                      min="5" max="95"
                      value={slot.ctaY}
                      onChange={(e) => handleCtaYChange(index, e.target.value)}
                      className={rangeInputClass + " accent-amber-500"}
                    />
                  </div>
                </div>
              )}
            </div>

          </div>
        ))}
      </div>

      <button id="downloadAllButtonReact" onClick={handleDownloadAll} className={`${greenButtonClass} text-base`}>
        Download All Processed Images (ZIP)
      </button>
      <p className="text-xs text-gray-400 mt-3 text-center">Note: URL-loaded images might fail due to CORS.</p>
      <p className="text-xs text-gray-400 mt-1 text-center">Ensure JSZip and FileSaver are available.</p>
    </div>
  );
}

export default ImageEditorPage;
